import {emptyArray, isNil, notNil} from '@packages/utilities/fp/utils';
import {curry} from 'lodash';

// @ts-expect-error - TS7006 - Parameter 'key' implicitly has an 'any' type. | TS7006 - Parameter 'structure' implicitly has an 'any' type.
export const get: any = curry((key, structure) => {
  const value = getElse(key, null, structure);

  if (isNil(value)) {
    throw new TypeError(
      `No value exists at the provided key '${key}'. Use Data.getElse if a ` +
        `key may not be associated with a value.`
    );
  }

  return value;
});

// @ts-expect-error - TS7006 - Parameter 'key' implicitly has an 'any' type. | TS7006 - Parameter 'defaultValue' implicitly has an 'any' type. | TS7006 - Parameter 'structure' implicitly has an 'any' type.
export const getElse: any = curry((key, defaultValue, structure) => {
  if (isNil(structure)) {
    throw new TypeError(`The structure passed to Data.getElse cannot be nil.`);
  }

  if (typeof structure.get === 'function') {
    const value = structure.get(key);
    return isNil(value) ? defaultValue : value;
  } else if (isIndexable(structure)) {
    return isNil(structure[key]) ? defaultValue : structure[key];
  } else {
    throw new TypeError(
      `The structure passed to Data.getElse must be either indexable or have ` +
        `a 'get' method defined. Received ${structure} instead.`
    );
  }
});

// Returns true for objects, including arrays.
export const isIndexable = (thing: unknown): boolean => thing instanceof Object;

// Table :: Map ID Record

// leftJoinWith :: (ID -> Table -> Record) -> (Record -> a) -> b -> Table -> Table -> Table
// @ts-expect-error - TS7006 - Parameter 'joinFn' implicitly has an 'any' type. | TS7006 - Parameter 'f' implicitly has an 'any' type. | TS7006 - Parameter 'key' implicitly has an 'any' type. | TS7006 - Parameter 'tableA' implicitly has an 'any' type. | TS7006 - Parameter 'tableB' implicitly has an 'any' type.
export const leftJoinWith: any = curry((joinFn, f, key, tableA, tableB) => {
  let keyPath = key;

  if (typeof key === 'string' || typeof key === 'number') {
    keyPath = Array.of(key);
  }

  // @ts-expect-error - TS7006 - Parameter 'record' implicitly has an 'any' type.
  return tableA.map((record) =>
    // @ts-expect-error - TS7006 - Parameter 'item' implicitly has an 'any' type.
    record.updateIn(keyPath, (item) => {
      if (item != null) {
        const joined = joinFn(item, tableB);
        if (notNil(joined)) {
          return f(joined);
        }
      }
      return item;
    })
  );
});

// leftJoin :: (Record -> a) -> b -> Table -> Table -> Table
// @ts-expect-error - TS7006 - Parameter 'k' implicitly has an 'any' type. | TS7006 - Parameter 's' implicitly has an 'any' type.
export const leftJoin: any = leftJoinWith((k, s) => getElse(k, null, s));

// Type used to label values/parameters that are
// raw evaluated data from a DOM node during rendering.
export type EvaluatedNodeData = unknown;

// Some utility functions for traversing data values in evaluated
// nodes.

/**
 * Retrieve a value by traversing an object graph starting at a data value.
 * It's assumed (but not required) that the data value is an evaluated
 * node produced during the rendering process.
 *
 * @param value the data value to traverse.
 * @param path an array of strings specifying the fields to traverse.
 * @return the value at the path, or undefined if no value exists at that path.
 */
export const getInPojo = (
  value: unknown,
  path: (string | number)[]
): unknown => {
  let current: any = value;
  for (const key of path) {
    if (!current) {
      return undefined;
    }
    current = current[key];
  }
  return current;
};

const getOrElse =
  <T>(getter: (data: unknown) => NonNullable<T> | undefined) =>
  (data: unknown, t: T): T =>
    getter(data) ?? t;

export const getNumber = (v: unknown): number | undefined =>
  typeof v === 'number' ? (v as number) : undefined;

export const getNumberOrElse = getOrElse(getNumber);

export const getString = (v: unknown): string | undefined =>
  typeof v === 'string' ? v : undefined;

export const getStringOrElse = getOrElse(getString);

export const getBoolean = (v: unknown): boolean | undefined =>
  v === true || v === false ? v : undefined;

export const getBooleanOrElse = getOrElse(getBoolean);

export const getArray = <T>(v: unknown): Array<T> | undefined =>
  Array.isArray(v) ? v : undefined;

export const getArrayOrElse = getOrElse(getArray);

export const getRecord = (v: unknown): Record<string, unknown> | undefined =>
  v && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : undefined;

export const getRecordOrElse = getOrElse(getRecord);

// There's a common idiom where we get a tag out of a data record,
// and then use that as a tag in a React element.
export const getTag = (v: unknown): React.ElementType | undefined =>
  typeof v === 'string' ? (v as React.ElementType) : undefined;

export const getTagOrElse = getOrElse(getTag);

export const getChildren = (v: unknown): EvaluatedNodeData[] => {
  if (
    v &&
    typeof v === 'object' &&
    'children' in v &&
    v.children &&
    Array.isArray(v.children)
  ) {
    return v.children as EvaluatedNodeData[];
  } else {
    return emptyArray;
  }
};

/**
 * Find the first child whose source element matches a predicate.
 *
 * Element children of evaluated node data are rendered React elements
 * that carry the element they were rendered from at `props.element`, so
 * the predicate is applied to that element. Non-element children (like
 * text) never match.
 */
export const findChild = <E>(
  v: unknown,
  elementPredicate: (element: E) => boolean
): EvaluatedNodeData | undefined =>
  getChildren(v).find((child) => {
    const element = getInPojo(child, ['props', 'element']);
    return notNil(element) && elementPredicate(element as E);
  });
